import * as React from 'react';
import { Box, Container, FormControlLabel, Grid2, Radio, RadioGroup, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../setup/Navbar';
import AutoComplete from '../../../BuiltInComponent/AutoComplete';
import ButtonNormal from '../../../BuiltInComponent/ButtonNormal';
import TextFields from '../../../BuiltInComponent/TextField';
import DatePickerSelf from '../../../BuiltInComponent/DatePricker';
import HeadingText from '../../../BuiltInComponent/HeadingTest';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Table, TableHead, TableBody, TableCell, TableRow, TableContainer, Paper } from '@mui/material';
import { getSupplierList, getItemList } from '../../../features/setup/useableSlice';
import { createPDMaster, getPDData, getPDDetails, getDraftPO, createPDDetail, getCurrentStock } from '../../../features/StockManagement/Purchases/pdSlice';
import BasicAlerts from '../../../BuiltInComponent/Alert'
import { createPurchaseIdempotencyKey } from '../../../utils/purchaseIdempotencyKey'
import ManualPurchaseItemPicker from './ManualPurchaseItemPicker';
import usePurchaseWorkflow from '../../../hooks/usePurchaseWorkflow';

export default function AddPurchaseOrder() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector(state => state.auth);
    const { suppliers, items } = useSelector(state => state.ru);
    const { startStage } = usePurchaseWorkflow();
    const { returnpdID, draftPO } = useSelector(state => state.pd);

    // const [pdID, setPdID] = React.useState('');
    const [showAlert, setShowAlert] = React.useState(false);
    const submitLock = React.useRef(false);
    const masterKeyRef = React.useRef(null);
    const [saving, setSaving] = React.useState(false);

    const [selectedPDID, setSelectedPDID] = React.useState(null);
    const [masterData, setMasterData] = React.useState({
        entDate: dayjs(new Date()).format('YYYY-MM-DD'),
        pdID: returnpdID || '',
        userID: user.userID,
        PartyCode: '',
        remarks: '',
        pdStatus: 'Pending',
        grnSource: 'po',
        supplierGrnRefNo: '',
        editStatus: false
    });

    const [detailList, setDetailList] = React.useState([]);
    const isDirectGRN = startStage === 'PD' || masterData.grnSource === 'direct';

    React.useEffect(() => {
        if (startStage === 'PD' && !masterData.pdID) {
            setMasterData(prev => ({ ...prev, grnSource: 'direct' }));
        }
    }, [startStage, masterData.pdID]);

    React.useEffect(() => {
        dispatch(getDraftPO());
        dispatch(getSupplierList());
        dispatch(getItemList('purchase'));
        dispatch(getCurrentStock());
    }, [dispatch]);

    React.useEffect(() => {
        const passedpdID = location.state?.pdID
        if (passedpdID) {
            dispatch(getPDData(passedpdID))
                .then(res => {
                    if (res.payload) {
                        if (res.payload.pm[0].pdStatus === 'Pending') {
                            setMasterData(prev => ({
                                ...prev,
                                pdID: res.payload.pm[0].pdID,
                                entDate: res.payload.pm[0].entDate,
                                PartyCode: res.payload.pm[0].PartyCode,
                                remarks: res.payload.pm[0].remarks,
                                pdStatus: res.payload.pm[0].pdStatus,
                                grnSource: res.payload.pm[0].grnSource || (res.payload.pd.some(item => !item.poID) ? 'direct' : 'po'),
                                supplierGrnRefNo: res.payload.pm[0].supplierGrnRefNo || '',
                                editStatus: true
                            }))
                            if (detailList.length === 0) {
                                const fetchedItems = res.payload.pd.map(item => ({
                                    ...item,
                                    quantity: item.quantity || '',
                                    delivered: item.delivered || '',
                                    poID: item.poID
                                }));
                                setDetailList(fetchedItems);
                            }
                        } else {
                            setShowAlert(true)
                            setTimeout(() => {
                                navigate('/GRN')
                            }, 3000);
                        }
                    } else {
                        setMasterData(prev => ({
                            ...prev,
                            pdID: returnpdID
                        }))
                    }
                })
        }
    }, [location.state?.pdID, navigate, dispatch, returnpdID, detailList.length]);

    const handleMasterChange = (data) => {
        const keys = Object.keys(data);
        setMasterData(prev => ({ ...prev, [keys[0]]: data[keys[0]] }));
    };

    const handlePRSelect = async (data) => {
        if (!data?.poID) return;
        console.log("Selected PR Data:", data);
        const poID = data.poID;
        const alreadyAdded = detailList.some(item => item.poID === poID);
        if (alreadyAdded) {
            alert(`PO #${poID} already added.`);
            return;
        }
        setSelectedPDID(poID);
        dispatch(getPDDetails(poID)).then(res => {
            if (res.payload) {
                const fetchedItems = res.payload.map(item => ({
                    ...item,
                    quantity: item.quantity || '',
                    delivered: item.quantity || '',
                    poID: poID
                }));
                setDetailList(prev => [...prev, ...fetchedItems]);
            }
        });
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...detailList];
        updated[index][field] = value;
        setDetailList(updated);
    };

    const saveMaster = async () => {
        if (submitLock.current) return;
        submitLock.current = true;
        setSaving(true);
        try {
            const isNew = !masterData.pdID;
            if (isNew && !masterKeyRef.current) masterKeyRef.current = createPurchaseIdempotencyKey('GRN', user?.userID);
            const result = await dispatch(createPDMaster({
                ...masterData,
                idempotencyKey: isNew ? masterKeyRef.current : null
            })).unwrap();
            setMasterData(prev => ({ ...prev, pdID: result.pdID }));
            if (isNew) masterKeyRef.current = null;
        } catch (error) {
            alert(`GRN save failed: ${error}`);
        } finally {
            setSaving(false);
            submitLock.current = false;
        }
    };

    const handleSourceChange = (event) => {
        const grnSource = event.target.value;
        setMasterData(prev => ({ ...prev, grnSource }));
        setSelectedPDID(null);
        setDetailList([]);
    };

    const saveDetails = async () => {
        if (submitLock.current) return;
        const validDetails = detailList.filter((row) => Number(row.delivered) > 0);
        if (validDetails.length === 0) {
            alert('Kam az kam aik item ki Delivered Qty zero se zyada enter karein.');
            return;
        }
        submitLock.current = true;
        setSaving(true);
        try {
            const result = await dispatch(createPDDetail({ pdID: masterData.pdID, detailData: validDetails, editStatus: masterData.editStatus })).unwrap();
            if (!result?.savedItems) throw new Error('Koi GRN item save nahi hua.');
            navigate('/GRN');
        } catch (error) {
            alert(`GRN details save failed: ${error}`);
        } finally {
            setSaving(false);
            submitLock.current = false;
        }
    };

    return (
        <>
            <Navbar />
            <Container>
                {showAlert && (
                    <BasicAlerts
                        msg="This Purchase Delivery Challan (GRN) is not in Delivered status and cannot be edited."
                        getCloseCommand={setShowAlert}
                    />
                )}
                <Grid2 container spacing={2}>
                    <Grid2 size={4} sx={{ marginTop: 3 }} ></Grid2>
                    <Grid2 size={4} sx={{ marginTop: 3 }} ><HeadingText text={'Purchase Delivery (GRN)'} /></Grid2>
                    <Grid2 size={4} sx={{ marginTop: 3 }} ></Grid2>
                    <Grid2 size={0.5}>
                        <KeyboardBackspaceIcon
                            sx={{
                                fontSize: 30, marginLeft: -1, color: 'white', cursor: 'pointer',
                                backgroundColor: '#1D4988', padding: 1, borderRadius: 50
                            }}
                            onClick={() => { navigate('/GRN') }} />
                    </Grid2>
                    <Grid2 size={12}>
                        <Typography sx={{
                            marginLeft: -1,
                            color: 'grey', fontSize: 18, fontWeight: 'bolder'
                        }}>Master Data</Typography>
                    </Grid2>

                    {startStage !== 'PD' && <Grid2 size={12}>
                        <Box sx={{ px: 2, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant='body2' fontWeight='bold'>GRN Source</Typography>
                            <RadioGroup row value={masterData.grnSource} onChange={handleSourceChange}>
                                <FormControlLabel value='po' control={<Radio size='small' />} label='Against PO' disabled={Boolean(masterData.pdID)} />
                                <FormControlLabel value='direct' control={<Radio size='small' />} label='Direct GRN (Without PO)' disabled={Boolean(masterData.pdID)} />
                            </RadioGroup>
                            {masterData.grnSource === 'direct' && (
                                <Typography variant='caption' color='text.secondary'>Supplier se direct receive hone wale items manually add karein.</Typography>
                            )}
                        </Box>
                    </Grid2>}

                    <Grid2 size={2}>
                        <DatePickerSelf name="entDate" value={masterData.entDate} onChange={handleMasterChange} />
                    </Grid2>
                    <Grid2 size={3}>
                        <AutoComplete
                            label="Select Party"
                            listData={suppliers && suppliers}
                            value={suppliers.find(p => p.PartyCode === masterData.PartyCode)}
                            onChange={handleMasterChange}
                            disabledValue={masterData.pdID ? true : false}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <TextFields
                            label="Supplier GRN Ref No"
                            name="supplierGrnRefNo"
                            value={masterData.supplierGrnRefNo}
                            onChange={handleMasterChange}
                            disStatus={false}
                        />
                    </Grid2>
                    <Grid2 size={2.5}>
                        <TextFields
                            label="Remarks"
                            name="remarks"
                            value={masterData.remarks}
                            onChange={handleMasterChange}
                            disStatus={false}
                        />
                    </Grid2>
                    <Grid2 size={2.5}>
                        <ButtonNormal text="Save PD (GRN) Master" onClick={saveMaster} loading={saving} disabled={saving} />
                    </Grid2>
                    {masterData.pdID && (
                        <>
                            <Grid2 size={12}>
                                <Typography sx={{
                                    marginLeft: -1,
                                    color: 'grey', fontSize: 18, fontWeight: 'bolder'
                                }}>Detail Data</Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 12, md: isDirectGRN ? 9 : 5, lg: isDirectGRN ? 8 : 4 }}>
                                {isDirectGRN ? <ManualPurchaseItemPicker items={items} stage="PD" onAdd={(row) => setDetailList((prev) => [...prev, row])} /> : <AutoComplete
                                    label="Select Purchase Order"
                                    listData={draftPO && draftPO}
                                    value={draftPO.find(p => p.poID === selectedPDID) || null}
                                    onChange={(data) => handlePRSelect(data)}
                                />}
                            </Grid2>
                            <Grid2 size={12}>
                                <EditablePOItemTable
                                    tableData={detailList && detailList}
                                    onFieldChange={handleItemChange}
                                />
                            </Grid2>

                            <Grid2 size={2}>
                                <ButtonNormal text="Save GRN Details" onClick={saveDetails} loading={saving} disabled={saving} />
                            </Grid2>
                        </>
                    )}
                </Grid2>
            </Container>
        </>
    );
}



// src/BuiltInComponent/EditablePOItemTable.jsx


function EditablePOItemTable({ tableData, onFieldChange }) {
    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 760, tableLayout: 'fixed' }}>
                <TableHead sx={{ backgroundColor: '#f2f2f2' }}>
                    <TableRow>
                        <TableCell sx={{ width: '13%' }}>Item Code</TableCell>
                        <TableCell sx={{ width: '35%' }}>Description</TableCell>
                        <TableCell sx={{ width: '18%' }}>Quantity</TableCell>
                        <TableCell sx={{ width: '20%' }}>Delivered Qty</TableCell>
                        <TableCell sx={{ width: '14%' }}>Balance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.map((row, index) => {
                        const balance = (parseFloat(row.quantity || 0) - parseFloat(row.delivered || 0)).toFixed(2);
                        return (
                            <TableRow key={index}>
                                <TableCell>{row.itemCode}</TableCell>
                                <TableCell>{row.itemDesc}</TableCell>

                                <TableCell>
                                    <TextFields
                                        name="quantity"
                                        value={row.quantity}
                                        onChange={(val) => onFieldChange(index, 'quantity', val.quantity)}
                                        disStatus={false}
                                    />
                                </TableCell>

                                <TableCell>
                                    <TextFields
                                        name="delivered"
                                        value={row.delivered}
                                        onChange={(val) => onFieldChange(index, 'delivered', val.delivered)}
                                        disStatus={false}
                                    />
                                </TableCell>

                                <TableCell>{balance}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
